import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { apiFetch } from '../utils/apiClient';
import { extractTenantIdFromLocation, resolveTenantId } from '../utils/tenantResolution';
import { getBackendBaseOrigin } from '../utils/network';

export interface MenuItemVariant {
    id: string;
    name: string;
    price: number;
    available?: boolean;
}

export interface MenuItem {
    id: string;
    nome: string;
    prezzo: number;
    categoria: string;
    sottocategoria?: string;
    immagine?: string;
    descrizione?: string;
    variants?: MenuItemVariant[];
}

export interface Location {
    id: string;
    nome: string;
}

export interface TenantInfo {
    id: string;
    nome: string;
    name: string;
    logoUrl?: string;
    backgroundImageUrl?: string;
    indirizzo?: string;
    telefono?: string;
    email?: string;
    sito?: string;
    orariApertura?: string;
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
    fontFamily?: string;
    description?: string;
    colori?: {
        primario: string;
        secondario: string;
        sfondo: string;
        testo: string;
    };
}

export interface TenantFeatures {
    orderingEnabled: boolean;
    paymentEnabled: boolean;
    multiLanguageEnabled: boolean;
    brandingEnabled: boolean;
    deliveryEnabled: boolean;
    deliveryPaymentEnabled: boolean;
    deliveryGuestEnabled: boolean;
    browseMenuEnabled: boolean;
}

interface TenantContextType {
    currentLocation: string | null;
    locations: Location[];
    menuData: MenuItem[];
    categories: { id: string; name: string; image?: string }[];
    subcategories?: { id: string; name: string; image?: string; categoryId: string }[];
    switchLocation: (loc: string) => void;
    t: (key: string) => string;
    loading: boolean;
    error: string | null;
    tenantId: string | null;
    tenantSlug: string | null;
    tenantInfo: TenantInfo | null;
    features: TenantFeatures | null;
    reloadData: () => Promise<void>;
    changeTenant: (newTenantId: string) => void;
    documents: any[];
    loadTenantDocuments: (tenantId: string) => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider = ({ children }: { children: ReactNode }) => {
    const toAbsoluteUploadUrl = (url?: string): string | undefined => {
        if (!url) return undefined;
        if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;

        const base = getBackendBaseOrigin();
        const normalizedUrl = url;

        if (normalizedUrl.startsWith('/uploads/')) {
            return `${base}${normalizedUrl}`;
        }
        if (normalizedUrl.startsWith('uploads/')) {
            return `${base}/${normalizedUrl}`;
        }

        return url;
    };

    const [locations, setLocations] = useState<Location[]>([]);
    const [currentLocation, setCurrentLocation] = useState<string | null>(null);
    const [menuData, setMenuData] = useState<MenuItem[]>([]);
    const [categories, setCategories] = useState<{ id: string; name: string; image?: string }[]>([]);
    const [subcategories, setSubcategories] = useState<{ id: string; name: string; image?: string; categoryId: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [tenantId, setTenantId] = useState<string | null>(null);
    const [tenantSlug, setTenantSlug] = useState<string | null>(() => {
        if (typeof window === 'undefined') return null;
        try {
            return localStorage.getItem('tenantSlug');
        } catch {
            return null;
        }
    });
    const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
    const [features, setFeatures] = useState<TenantFeatures | null>(null);
    const [documents, setDocuments] = useState<any[]>([]);

    const updateTenantSelection = useCallback((finalId: string, slug?: string) => {
        localStorage.setItem('tenantId', finalId);
        if (slug) {
            localStorage.setItem('tenantSlug', slug);
            setTenantSlug(slug);
        } else {
            localStorage.removeItem('tenantSlug');
            setTenantSlug(null);
        }
        setTenantId(finalId);
    }, []);

    const normalizeTenantId = useCallback(async (rawTenantId: string): Promise<{ finalId: string; slug?: string } | null> => {
        const trimmed = (rawTenantId ?? '').trim();
        if (!trimmed) return null;
        if (/^\d+$/.test(trimmed)) {
            return { finalId: trimmed };
        }

        try {
            const resolved = await resolveTenantId(trimmed);
            if (resolved) {
                return { finalId: resolved, slug: trimmed };
            }
        } catch {
            // ignore resolution errors and let caller handle fallback
        }
        return null;
    }, []);

    const envVars = process.env as Record<string, string | undefined>;
    const rawDefaultTenantId = envVars['REACT_APP_DEFAULT_TENANT_ID'] ?? envVars['REACT_APP_TENANT_ID'];
    const defaultTenantId = typeof rawDefaultTenantId === 'string' ? rawDefaultTenantId.trim() : '';

    // Resolve tenantId
    useEffect(() => {
        let cancelled = false;

        const attemptResolve = async (rawId: string, rememberSlug: boolean = false): Promise<boolean> => {
            const normalized = await normalizeTenantId(rawId);
            if (!normalized) return false;
            if (cancelled) return true;
            updateTenantSelection(normalized.finalId, rememberSlug ? normalized.slug : undefined);
            setError(null);
            return true;
        };

        const bootstrapTenant = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const tenantIdFromUrl = urlParams.get('tenantId') || urlParams.get('tenantid');
            if (tenantIdFromUrl) {
                const ok = await attemptResolve(tenantIdFromUrl, true);
                if (!ok && !cancelled) setError('Tenant non trovato');
                return;
            }

            const candidate = extractTenantIdFromLocation(window.location.hostname, window.location.pathname);
            if (candidate) {
                const ok = await attemptResolve(candidate, true);
                if (ok) return;
            }

            const storedTenantId = localStorage.getItem('tenantId');
            if (storedTenantId) {
                const ok = await attemptResolve(storedTenantId, true);
                if (ok) return;
                localStorage.removeItem('tenantId');
                localStorage.removeItem('tenantSlug');
            }

            if (defaultTenantId) {
                const ok = await attemptResolve(defaultTenantId, true);
                if (!ok && !cancelled) setError('Tenant non trovato');
                return;
            }

            if (!cancelled) setError('Nessun tenant selezionato');
        };

        bootstrapTenant();

        return () => {
            cancelled = true;
        };
    }, [defaultTenantId, normalizeTenantId, updateTenantSelection]);

    const changeTenant = useCallback((newTenantId: string) => {
        const trimmed = (newTenantId ?? '').trim();
        if (!trimmed || trimmed === tenantId) return;

        (async () => {
            setError(null);
            const normalized = await normalizeTenantId(trimmed);
            if (!normalized) {
                setError('Tenant non trovato');
                return;
            }

            updateTenantSelection(normalized.finalId, normalized.slug);

            try {
                const sp = new URLSearchParams(window.location.search);
                sp.delete('tenantid');
                sp.set('tenantId', trimmed);
                const base = window.location.pathname.split('?')[0];
                const newUrl = `${base}?${sp.toString()}`;
                window.history.pushState({}, '', newUrl);
            } catch {
                // ignore history errors
            }
        })();
    }, [tenantId, normalizeTenantId, updateTenantSelection]);

    const reloadData = async (): Promise<void> => {
        if (loading) return; // evita rientri mentre sta già caricando
        setLoading(true);
        setError(null);
        setTenantInfo(null);
        setFeatures(null);
        setCategories([]);
        setSubcategories([]);
        setMenuData([]);

        const currentTenantId = localStorage.getItem('tenantId');
        if (!currentTenantId) {
            setError('Nessun tenant selezionato');
            setLoading(false);
            return;
        }

        // OTTIMIZZAZIONE: Parallelizzazione delle chiamate API
        // Invece di fare 4 chiamate sequenziali, le facciamo tutte in parallelo
        try {
            const [tenantInfoRes, brandingRes, featuresRes, snapRes] = await Promise.allSettled([
                apiFetch(`/api/public/tenants/${currentTenantId}`, {
                    tenantId: currentTenantId,
                    cache: 'no-cache',
                }),
                apiFetch(`/api/public/tenants/${currentTenantId}/branding`, {
                    tenantId: currentTenantId,
                    cache: 'no-cache',
                }),
                apiFetch(`/api/public/tenants/${currentTenantId}/features`, {
                    tenantId: currentTenantId,
                    cache: 'no-cache',
                }),
                apiFetch('/api/menu/snapshot', {
                    tenantId: currentTenantId,
                    cache: 'no-cache'
                })
            ]);

            // Processa tenant info (CRITICO - se fallisce, ferma tutto)
            if (tenantInfoRes.status === 'fulfilled' && tenantInfoRes.value.ok) {
                const txt = await tenantInfoRes.value.text();
                const data = JSON.parse(txt);
                const resolvedSlug = typeof data.slug === 'string' && data.slug.trim() ? data.slug.trim() : null;
                if (resolvedSlug) {
                    setTenantSlug(resolvedSlug);
                    try {
                        localStorage.setItem('tenantSlug', resolvedSlug);
                    } catch {
                        // ignore storage errors
                    }
                } else {
                    setTenantSlug(null);
                    try {
                        localStorage.removeItem('tenantSlug');
                    } catch {
                        // ignore storage errors
                    }
                }
                const baseInfo: TenantInfo = {
                    id: data.id || currentTenantId,
                    nome: data.name || data.nome || 'Tenant',
                    name: data.name || data.nome || 'Tenant',
                    primaryColor: '#2196f3',
                    secondaryColor: '#f50057',
                    backgroundColor: '#ffffff',
                    textColor: '#333333',
                    fontFamily: 'Roboto, sans-serif',
                    description: '',
                };
                setTenantInfo(baseInfo);

                // Processa branding (NON CRITICO - aggiorna se disponibile)
                if (brandingRes.status === 'fulfilled' && brandingRes.value.ok) {
                    try {
                        const brandingTxt = await brandingRes.value.text();
                        const branding = JSON.parse(brandingTxt);
                        const logoUrl = toAbsoluteUploadUrl(branding.logoUrl);
                        const bgImageUrl = toAbsoluteUploadUrl(branding.backgroundImageUrl);
                        setTenantInfo(prev =>
                            prev
                                ? {
                                    ...prev,
                                    // Mantieni il nome originale del tenant, non sostituirlo con il branding
                                    // name: prev.name, // Mantieni il nome originale
                                    // nome: prev.nome, // Mantieni il nome originale
                                    description: branding.description || prev.description,
                                    logoUrl: logoUrl || prev.logoUrl,
                                    backgroundImageUrl: bgImageUrl || prev.backgroundImageUrl,
                                    primaryColor: branding.primaryColor || prev.primaryColor,
                                    secondaryColor: branding.secondaryColor || prev.secondaryColor,
                                    backgroundColor: branding.backgroundColor || prev.backgroundColor,
                                    textColor: branding.textColor || prev.textColor,
                                    fontFamily: branding.fontFamily || prev.fontFamily,
                                }
                                : prev
                        );
                    } catch {
                        // Branding non critico, continua senza errore
                    }
                }
            } else {
                setError('Tenant non trovato');
                setLoading(false);
                return;
            }

            // Processa features (NON CRITICO - usa defaults se fallisce)
            if (featuresRes.status === 'fulfilled' && featuresRes.value.ok) {
                try {
                    const featuresTxt = await featuresRes.value.text();
                    const f = JSON.parse(featuresTxt);
                    setFeatures({
                        orderingEnabled: !!f.orderingEnabled,
                        paymentEnabled: !!f.paymentEnabled,
                        multiLanguageEnabled: !!f.multiLanguageEnabled,
                        brandingEnabled: !!f.brandingEnabled,
                        deliveryEnabled: !!f.deliveryEnabled,
                        deliveryPaymentEnabled: !!f.deliveryPaymentEnabled,
                        deliveryGuestEnabled: f.deliveryGuestEnabled !== undefined ? !!f.deliveryGuestEnabled : true,
                        browseMenuEnabled: f.browseMenuEnabled !== undefined ? !!f.browseMenuEnabled : true,
                    });
                } catch {
                    // Use defaults se parsing fallisce
                    setFeatures({
                        orderingEnabled: false,
                        paymentEnabled: false,
                        multiLanguageEnabled: true,
                        brandingEnabled: true,
                        deliveryEnabled: false,
                        deliveryPaymentEnabled: false,
                        deliveryGuestEnabled: true,
                        browseMenuEnabled: true,
                    });
                }
            } else {
                setFeatures({
                    orderingEnabled: false,
                    paymentEnabled: false,
                    multiLanguageEnabled: true,
                    brandingEnabled: true,
                    deliveryEnabled: false,
                    deliveryPaymentEnabled: false,
                    deliveryGuestEnabled: true,
                    browseMenuEnabled: true,
                });
            }

            // Processa menu snapshot (CRITICO per il funzionamento)
            if (snapRes.status === 'fulfilled' && snapRes.value.ok) {
                try {
                    const rawText = await snapRes.value.text();
                    let parsed: any;
                    try {
                        parsed = JSON.parse(rawText);
                    } catch (e) {
                        throw new Error('JSON snapshot non valido');
                    }

                    let mappedCats: { id: string; name: string; image?: string }[] = [];
                    let mappedSubs: { id: string; name: string; image?: string; categoryId: string }[] = [];
                    let products: any[] = [];

                    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && (parsed.categories || parsed.products)) {
                        // New snapshot structure
                        const cats = Array.isArray(parsed.categories) ? parsed.categories : [];
                        const subs = Array.isArray(parsed.subcategories) ? parsed.subcategories : [];
                        const prods = Array.isArray(parsed.products) ? parsed.products : [];
                        mappedCats = cats.map((c: any) => ({ id: String(c.id), name: c.name || c.nome || String(c.id), image: toAbsoluteUploadUrl(c.imageUrl || c.immagine) }));
                        mappedSubs = subs.map((s: any) => ({ id: String(s.id), name: s.name || s.nome || String(s.id), image: toAbsoluteUploadUrl(s.imageUrl || s.immagine), categoryId: String(s.categoryId || (s.category && s.category.id) || '') }));
                        products = prods;
                    } else if (Array.isArray(parsed)) {
                        // Legacy tree structure fallback
                        const itemsCollected: any[] = [];
                        parsed.forEach((node: any) => {
                            const cat = node.category;
                            if (cat) {
                                const catId = cat.id ? String(cat.id) : '';
                                if (catId) {
                                    const catImageUrl = toAbsoluteUploadUrl(cat.imageUrl || cat.immagine);
                                    mappedCats.push({ id: catId, name: cat.name || cat.nome || catId, image: catImageUrl });
                                }
                            }
                            if (Array.isArray(node.items)) node.items.forEach((it: any) => itemsCollected.push(it));
                            if (Array.isArray(node.subcategories)) {
                                node.subcategories.forEach((subNode: any) => {
                                    const sub = subNode.subcategory;
                                    if (sub) {
                                        const subId = sub.id ? String(sub.id) : '';
                                        const categoryId = sub.category && sub.category.id ? String(sub.category.id) : (cat && cat.id ? String(cat.id) : '');
                                        const subImageUrl = toAbsoluteUploadUrl(sub.imageUrl || sub.immagine);
                                        mappedSubs.push({ id: subId, name: sub.name || sub.nome || subId, image: subImageUrl, categoryId });
                                    }
                                    if (Array.isArray(subNode.items)) subNode.items.forEach((it: any) => itemsCollected.push(it));
                                });
                            }
                        });
                        products = itemsCollected;
                    } else {
                        throw new Error('Formato snapshot inatteso');
                    }

                    setCategories(mappedCats);
                    setSubcategories(mappedSubs);

                    const normalized: MenuItem[] = products
                        .map((it: any, index: number) => {
                            if (!it) return null;
                            const itemImageUrl = toAbsoluteUploadUrl(it.imageUrl || it.immagine);
                            let categoria = '';
                            let sottocategoria = '';
                            if (it.categoryId) categoria = String(it.categoryId);
                            else if (it.category && it.category.id) categoria = String(it.category.id);
                            else if (it.categoria) categoria = String(it.categoria);
                            if (it.subcategoryId) sottocategoria = String(it.subcategoryId);
                            else if (it.subcategory && it.subcategory.id) sottocategoria = String(it.subcategory.id);
                            const productId = it.id != null ? String(it.id) : (it.productId != null ? String(it.productId) : `prod-${index + 1}`);
                            const descriptionRaw = it.description ?? it.descrizione;
                            const variants = Array.isArray(it.variants)
                                ? it.variants
                                    .filter(Boolean)
                                    .map((variant: any, variantIndex: number) => {
                                        const variantId = variant.id != null
                                            ? String(variant.id)
                                            : `${productId}-var-${variantIndex + 1}`;
                                        return {
                                            id: variantId,
                                            name: variant.name || variant.nome || `Opzione extra ${variantIndex + 1}`,
                                            price: Number(variant.price ?? variant.prezzo ?? 0),
                                            available: variant.available !== false,
                                        } as MenuItemVariant;
                                    })
                                : undefined;
                            return {
                                id: productId,
                                nome: String(it.name || it.nome || `Prodotto ${index + 1}`),
                                prezzo: Number(it.price || it.prezzo || 0),
                                categoria,
                                sottocategoria: sottocategoria || undefined,
                                immagine: itemImageUrl || '',
                                descrizione: descriptionRaw != null ? String(descriptionRaw) : undefined,
                                variants,
                            } as MenuItem;
                        })
                        .filter(Boolean) as MenuItem[];
                    setMenuData(normalized);
                } catch (err: any) {
                    setError(`Errore menu snapshot: ${err?.message || 'Errore sconosciuto'}`);
                }
            } else {
                setError('Errore nel caricamento del menu');
            }

        } catch (err: any) {
            // Errore generale nelle chiamate API parallele
            setError(err?.message || 'Errore nel caricamento dei dati');
        } finally {
            if (process.env.NODE_ENV !== 'production') {
                try {
                    (window as any).__tenantDebug = {
                        tenantId: currentTenantId,
                        tenantInfo,
                        features,
                        categories,
                        subcategories,
                        menuData,
                    };
                } catch { }
            }
            setLoading(false);
        }
    };
    useEffect(() => {
        if (tenantId) {
            reloadData();
        }
        const onStorage = (e: StorageEvent) => {
            if (e.key === 'tenantId') {
                const newTenantId = localStorage.getItem('tenantId');
                setTenantId(newTenantId);
            }
            if (e.key === 'tenantSlug') {
                setTenantSlug(e.newValue ?? null);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, [tenantId]);

    // Carica documenti pubblici del tenant e crea una location fallback se necessario
    const loadTenantDocuments = async (tid: string) => {
        try {
            const params = new URLSearchParams();
            params.set('ts', Date.now().toString());
            const res = await apiFetch(`/api/public/tenants/${tid}/documents?${params.toString()}`, {
                tenantId: String(tid),
                cache: 'no-store',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });
            if (res.ok) {
                const data = await res.json();
                setDocuments(Array.isArray(data) ? data : []);
            } else {
                setDocuments([]);
            }
        } catch (e) {
            setDocuments([]);
        }
        // fallback single-location se le locations non sono fornite dal backend
        setLocations(prev => (prev && prev.length > 0) ? prev : [{ id: String(tid), nome: tenantInfo?.nome || `Locale ${tid}` }]);
    };

    // Effetto per caricare documenti quando cambia il tenant
    useEffect(() => {
        if (tenantId && tenantInfo) {
            loadTenantDocuments(tenantId);
            // ... altri loader esistenti
        }
    }, [tenantId, tenantInfo]);

    const switchLocation = (loc: string) => setCurrentLocation(loc);
    const t = (key: string) => key;

    return (
        <TenantContext.Provider
            value={{
                currentLocation,
                locations,
                menuData,
                categories,
                // expose subcategories in context for UI that needs it
                subcategories,
                switchLocation,
                t,
                loading,
                error,
                tenantId,
                tenantSlug,
                tenantInfo,
                features,
                reloadData,
                changeTenant,
                documents,
                loadTenantDocuments,
            }}
        >
            {children}
        </TenantContext.Provider>
    );
};

export const useTenant = () => {
    const context = useContext(TenantContext);
    if (!context) throw new Error('useTenant must be used within a TenantProvider');
    return context;
};

export default TenantContext;